import { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  knowledgeDocInstanceMatchesOcrId,
  parseViewerBoxId,
} from 'features/knowledgeDocumentViewer/utils/viewerOcrBoxId'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import { KnowledgeDocumentEntry, RolodexEntryType } from 'types'

export const useBoxLinkedDocuments = (
  knowledgeDocumentEntriesForSelectedDocument: KnowledgeDocumentEntry[],
  tooltipBox: Box | null,
  siteID: string
) => {
  const { currentPageNumber } = useKnowledgeDocumentUXContext()
  const systemDocumentEntryId =
    useDocumentTabDataContextOptional()?.systemDocumentEntryId
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const taggedUnit = useMemo(() => {
    if (!tooltipBox) return undefined

    const parsed = parseViewerBoxId(tooltipBox.id)
    if (!parsed) return undefined
    const { blockId, spreadPageIndex } = parsed

    return knowledgeDocumentEntriesForSelectedDocument?.find(
      (document: KnowledgeDocumentEntry) =>
        knowledgeDocInstanceMatchesOcrId(
          document.entry.data.fields.instance_id,
          blockId
        ) &&
        Number(document.entry.data.fields.page_number) ===
          currentPageNumber + spreadPageIndex
    )?.entry.tags.unit
  }, [
    currentPageNumber,
    knowledgeDocumentEntriesForSelectedDocument,
    tooltipBox,
  ])

  const connectedKnowledgeDocumentLoadOptions = useMemo(() => {
    if (isPipelineConfigScopeLoading || !taggedUnit) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({
      site: siteID,
      unit: taggedUnit,
    })
    if (!scopedTags) return undefined

    return {
      type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
      tags: scopedTags,
      limit: 1000000,
    }
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    siteID,
    taggedUnit,
  ])
  const { data: connectedKnowledgeDocumentEntries } =
    useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>(
      connectedKnowledgeDocumentLoadOptions
    )

  const filteredConnectedKnowledgeDocumentEntries = useMemo(() => {
    return connectedKnowledgeDocumentEntries?.filter(
      (entry) => entry.entry.tags['system-document'] !== systemDocumentEntryId
    )
  }, [systemDocumentEntryId, connectedKnowledgeDocumentEntries])

  return {
    connectedKnowledgeDocumentEntries:
      filteredConnectedKnowledgeDocumentEntries ?? [],
    taggedUnit,
  }
}
